import { useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { AccessLevel } from '@/entities/vfo/types';
import { useNotices } from '@/features/notices/useNotices';
import { useApi } from '@/shared/api';

import { useAnalyticsStore } from '../stores/analytics.store';
import { useCasesStore } from '../stores/cases.store';
import { useDocumentsStore } from '../stores/documents.store';
import { useMembersStore } from '../stores/members.store';
import { usePermissionsStore } from '../stores/permissions.store';
import { useTasksStore } from '../stores/tasks.store';

import { useIsFounder } from './useIsFounder';
import { usePermissions } from './usePermissions';

export const useAnalytics = (caseId?: string) => {
  const {
    isLoading,
    data,
    caseData,
    isInitialized,
    setLoading,
    setData,
    setInitialized,
    setCaseData,
    clearCaseData,
  } = useAnalyticsStore();
  const { members } = useMembersStore();
  const { hasAnyRole } = usePermissions();
  const { isInitialized: permissionsInitialized } = usePermissionsStore();
  const isFounder = useIsFounder();

  const caseExists = useCasesStore(useShallow((state) => state.cases.some((c) => c.id === caseId)));
  const newDocuments = useDocumentsStore(
    useShallow((state) => state.documents.map((d) => d.isNew)),
  );
  const caseMembers = useCasesStore(
    useShallow((state) => state.cases.map((c) => c.members.length)),
  );
  const tasks = useTasksStore(useShallow((state) => state.tasks));

  const api = useApi();
  const notices = useNotices();

  const isFirstMount = useRef(true);

  const fetchAnalytics = async () => {
    setLoading(true);
    const res = await api.dashboardGetAll();
    setLoading(false);
    setInitialized(true);

    if (res.error || !res.data) {
      notices.error('Failed to fetch analytics');
      return;
    }

    setData(res.data);
  };

  const fetchCaseAnalytics = async (targetCaseId: string) => {
    if (caseData[targetCaseId]) {
      return caseData[targetCaseId];
    }

    const res = await api.dashboardGetAll({ caseId: targetCaseId });
    if (res.error || !res.data) {
      const exists = useCasesStore.getState().cases.some((c) => c.id === targetCaseId);

      if (exists) {
        notices.error('Failed to fetch analytics');
      }

      return null;
    }
    setCaseData(targetCaseId, res.data);
    return res.data;
  };

  useEffect(() => {
    if ((!isFounder && !hasAnyRole([AccessLevel.EXECUTIVE])) || !permissionsInitialized) return;

    if (caseId) {
      if (!caseData[caseId] && caseExists) {
        fetchCaseAnalytics(caseId);
      }
    } else if (!isInitialized && !isLoading) {
      fetchAnalytics();
    }
  }, [
    caseId,
    caseExists,
    caseMembers,
    members.length,
    isFounder,
    hasAnyRole,
    caseData,
    isInitialized,
    isLoading,
    permissionsInitialized,
  ]);

  useEffect(() => {
    if (!permissionsInitialized) return undefined;
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return undefined;
    }
    if (caseId && !caseExists) return undefined;

    const timerId = setTimeout(() => {
      clearCaseData();
      setInitialized(false);
      if (isFounder || hasAnyRole([AccessLevel.EXECUTIVE])) {
        fetchAnalytics();
      }
    }, 500);

    return () => clearTimeout(timerId);
  }, [
    caseId,
    caseExists,
    caseMembers,
    members.length,
    newDocuments,
    tasks,
    permissionsInitialized,
  ]);

  const analyticsData = useMemo(() => {
    return caseId ? caseData[caseId] : data;
  }, [caseId, caseData, data]);

  return {
    analyticsData,
    isLoading,
    fetchAnalytics,
    fetchCaseAnalytics,
  };
};
